import { UIPagination, UITable } from '../../components/index';
import { UIDynamicTable } from '../../components/table/ui-dynamic-table';
import type { ComponentPlugin } from '../../components/ui-element.type';
import { createElement, dispatchCustomEvent } from '../../global/ui-helpers';

/**
 * @class
 * @memberof SharedComponents
 * @classdesc Class for ui-dynamic-table pagination plugin.
 * @alias UIPaginationPlugin
 * @implements {ComponentPlugin}
 * @param {UIDynamicTable} target Target instance.
 * @property {UIDynamicTable|HTMLElement} target Target instance.
 */
export class UIPaginationPlugin implements ComponentPlugin {
    static readonly MIN_PAGE_SIZE = 10;
    static readonly DEFAULT_PAGE_SIZE = 20;
    readonly target: UIDynamicTable;

    constructor(target: UIDynamicTable) {
        this.target = target;
    }

    get primaryControl(): UIPagination {
        return this.target.querySelector<UIPagination>(
            'ui-pagination[key=primary]'
        )!;
    }

    get secondaryControl(): UIPagination {
        return this.target.querySelector<UIPagination>(
            'ui-pagination[key=secondary]'
        )!;
    }

    modifyTable(uiTable: UITable): UITable {
        return uiTable;
    }

    /**
     * Handle UIPagination change event
     * @param {Event} e
     */
    handleChangePage(e: Event): void {
        const paginationTarget = e.target as UIPagination;
        const currentPage = paginationTarget.currentPage;
        const pageSize = paginationTarget.pageSize;

        const nextControl =
            this.primaryControl !== paginationTarget
                ? this.primaryControl
                : this.secondaryControl;

        nextControl.currentPage = currentPage;
        nextControl.pageSize = pageSize;

        dispatchCustomEvent(this.target, 'pagination-change', {
            currentPage: currentPage,
            pageSize: pageSize,
        });
    }

    /**
     * @param {number} currentPage
     * @param {number} pageSize
     * @param {number} total
     */
    update(currentPage: number, pageSize: number, total: number): void {
        [this.primaryControl, this.secondaryControl].forEach((control) => {
            if (total <= UIPaginationPlugin.MIN_PAGE_SIZE) {
                control.hide();
            } else {
                control.show();
            }
            control.pageSize = pageSize;
            control.total = total;
            control.currentPage = currentPage;
        });
        if (total > UIPaginationPlugin.MIN_PAGE_SIZE) {
            const visibleInCurrentPage = total - (currentPage - 1) * pageSize;
            if (visibleInCurrentPage >= UIPaginationPlugin.MIN_PAGE_SIZE) {
                this.primaryControl.show();
            } else {
                this.secondaryControl.hide();
            }
        }
    }

    /**
     * Inserts a pagination element before and after the table element
     * @inheritDoc
     */
    render(): void {
        const primaryPagination = createElement<UIPagination>({
            tagName: 'ui-pagination',
            attributes: {
                'key': 'primary',
                'show-page-size': 'show-page-size',
                'justify-desktop': 'left',
            },
        })!;

        const secondaryPagination = createElement<UIPagination>({
            tagName: 'ui-pagination',
            attributes: {
                'key': 'secondary',
                'show-page-size': 'show-page-size',
                'page-size': UIPaginationPlugin.DEFAULT_PAGE_SIZE,
                'justify-desktop': 'left',
            },
        })!;

        const pageSize = this.target.dataset.pagesize;
        if (pageSize) {
            primaryPagination.pageSize = Number(pageSize);
            secondaryPagination.pageSize = Number(pageSize);
        }
        try {
            const pageSizeList = JSON.parse(
                this.target.dataset.pagesizeOptions!
            ) as Array<number>;
            if (Array.isArray(pageSizeList)) {
                primaryPagination.pageSizeList = pageSizeList;
                secondaryPagination.pageSizeList = pageSizeList;
            }
        } catch (e) {
            // No options provided
        }

        const total = this.target.dataset.totalRecords;
        if (total) {
            primaryPagination.total = Number(total);
            secondaryPagination.total = Number(total);
        } else {
            // FIXME: Otherwise no event will be dispatched from ui-pagination
            primaryPagination.total = 1;
            secondaryPagination.total = 1;
        }

        primaryPagination.currentPage = 1;
        secondaryPagination.currentPage = 1;

        this.target.insertAdjacentElement('afterbegin', secondaryPagination);
        this.target.insertAdjacentElement('beforeend', primaryPagination);
    }

    hydrate(): void {
        this.primaryControl.addEventListener(
            'change',
            this.handleChangePage.bind(this)
        );
        this.secondaryControl.addEventListener(
            'change',
            this.handleChangePage.bind(this)
        );
    }
}
